import { fmtDate, fmtDateTime, rupiah, isPackageActive } from "@/lib/format";
import { Pill } from "@/components/Badges";
import { sessionsLabel } from "@/components/PackageCard";

const PKG_OPTS = { url: "/admin/packages", label: (p) => p.name };
const COURSE_OPTS = { url: "/admin/courses", label: (c) => c.title };
const TEACHER_OPTS = { url: "/admin/teachers", label: (t) => t.name };
const PKG_HINT = "Kosongkan untuk semua paket.";

const pkgNames = (row, lookup) => (row.package_ids?.length ? row.package_ids.map((id) => lookup("package_ids", id)).filter(Boolean).join(", ") : "Semua paket");

export const USER_FIELDS = [
  { name: "name", label: "Nama lengkap", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "role", label: "Role", type: "select", default: "siswa", options: [{ value: "siswa", label: "Siswa" }, { value: "pengajar", label: "Pengajar" }, { value: "admin", label: "Admin" }] },
  { name: "password", label: "Kata sandi", type: "password", hint: "Wajib saat membuat akun. Saat mengubah, kosongkan jika tidak diganti." },
  { name: "phone", label: "No. HP" },
  { name: "school", label: "Asal sekolah / instansi" },
  { name: "package_id", label: "Paket aktif", type: "select", allowEmpty: true, emptyLabel: "— Tanpa paket —", optionsFrom: PKG_OPTS },
  { name: "package_expires_at", label: "Masa berlaku hingga", type: "datetime" },
  { name: "active", label: "Akun aktif", type: "checkbox", default: true },
];

export const USER_COLUMNS = [
  { label: "Nama", render: (r) => <div><p className="font-medium text-slate-900">{r.name}</p><p className="text-xs text-slate-500">{r.email}</p></div> },
  { label: "Role", render: (r) => <Pill tone={r.role === "admin" ? "navy" : r.role === "pengajar" ? "amber" : "slate"}>{r.role}</Pill> },
  { label: "Paket", render: (r, lookup) => (r.role === "siswa" ? lookup("package_id", r.package_id) || "-" : "-") },
  { label: "Masa aktif", render: (r) => (r.role === "siswa" ? <div><p>{fmtDate(r.package_expires_at)}</p>{r.package_id && <Pill tone={isPackageActive(r) ? "green" : "red"}>{isPackageActive(r) ? "Aktif" : "Habis"}</Pill>}</div> : "-") },
  { label: "Status", render: (r) => <Pill tone={r.active ? "green" : "red"}>{r.active ? "Aktif" : "Nonaktif"}</Pill> },
];

export const PACKAGE_FIELDS = [
  { name: "code", label: "Kode (unik, mis. gold)", required: true },
  { name: "name", label: "Nama paket", required: true },
  { name: "category", label: "Kategori", type: "select", default: "Akademik", options: ["Akademik", "TNI/POLRI", "Paket", "Kedinasan"].map((v) => ({ value: v, label: v })) },
  { name: "price", label: "Harga (Rp)", type: "number" },
  { name: "duration_days", label: "Masa aktif (hari)", type: "number", default: 30 },
  { name: "sessions", label: "Jumlah pertemuan", placeholder: "mis. 20 atau sampai dengan H-1 tes", hint: "Isi angka (mis. 20) atau teks bebas (mis. sampai dengan H-1 tes)." },
  { name: "description", label: "Deskripsi", type: "textarea" },
  { name: "features", label: "Fitur (satu per baris)", type: "list" },
  { name: "order", label: "Urutan tampil", type: "number" },
  { name: "highlight", label: "Tandai sebagai unggulan", type: "checkbox" },
  { name: "active", label: "Tampilkan di website", type: "checkbox", default: true },
];

export const PACKAGE_COLUMNS = [
  { label: "Paket", render: (r) => <div><p className="font-medium text-slate-900">{r.name}</p><p className="text-xs text-slate-500">{r.category} · {r.code}</p></div> },
  { label: "Harga", render: (r) => rupiah(r.price) },
  { label: "Masa aktif", render: (r) => `${r.duration_days} hari` },
  { label: "Pertemuan", render: (r) => sessionsLabel(r.sessions) || "-" },
  { label: "Status", render: (r) => <Pill tone={r.active ? "green" : "slate"}>{r.active ? "Tampil" : "Disembunyikan"}</Pill> },
];

export const COURSE_FIELDS = [
  { name: "title", label: "Nama mata pelajaran / kurikulum", required: true },
  { name: "description", label: "Deskripsi", type: "textarea" },
  { name: "teacher_id", label: "Pengajar", type: "select", allowEmpty: true, optionsFrom: TEACHER_OPTS },
  { name: "package_ids", label: "Akses paket", type: "multiselect", optionsFrom: PKG_OPTS, hint: PKG_HINT },
];

export const COURSE_COLUMNS = [
  { label: "Kurikulum", render: (r) => <div><p className="font-medium text-slate-900">{r.title}</p><p className="text-xs text-slate-500 line-clamp-1">{r.description}</p></div> },
  { label: "Pengajar", render: (r, lookup) => lookup("teacher_id", r.teacher_id) || "-" },
  { label: "Akses", render: pkgNames },
];

export const MATERIAL_FIELDS = [
  { name: "course_id", label: "Kurikulum", type: "select", required: true, optionsFrom: COURSE_OPTS },
  { name: "title", label: "Judul materi", required: true },
  { name: "type", label: "Jenis", type: "select", default: "video", options: [{ value: "video", label: "Video (YouTube)" }, { value: "pdf", label: "Modul PDF" }, { value: "text", label: "Teks / ringkasan" }] },
  { name: "youtube_url", label: "Link YouTube", placeholder: "https://www.youtube.com/watch?v=...", showIf: (f) => f.type === "video" },
  { name: "file", label: "File modul (PDF)", type: "file", showIf: (f) => f.type === "pdf" },
  { name: "content", label: "Isi / catatan", type: "textarea" },
  { name: "order", label: "Urutan", type: "number" },
];

export const MATERIAL_COLUMNS = [
  { label: "Materi", render: (r) => <p className="font-medium text-slate-900">{r.title}</p> },
  { label: "Kurikulum", render: (r, lookup) => lookup("course_id", r.course_id) || "-" },
  { label: "Jenis", render: (r) => <Pill>{r.type}</Pill> },
  { label: "Urutan", key: "order" },
];

export const QUESTION_FIELDS = [
  { name: "course_id", label: "Kurikulum", type: "select", allowEmpty: true, optionsFrom: COURSE_OPTS },
  { name: "topic", label: "Topik" },
  { name: "text", label: "Pertanyaan", type: "textarea", required: true },
  { name: "options", label: "Pilihan jawaban (satu per baris, urut A–E)", type: "list", required: true },
  { name: "answer_index", label: "Kunci jawaban", type: "select", numeric: true, default: 0, options: "ABCDE".split("").map((l, i) => ({ value: i, label: l })) },
  { name: "explanation", label: "Pembahasan", type: "textarea" },
];

export const QUESTION_COLUMNS = [
  { label: "Pertanyaan", render: (r) => <p className="max-w-md line-clamp-2 text-slate-900">{r.text}</p> },
  { label: "Topik", render: (r, lookup) => <div><p>{r.topic || "-"}</p><p className="text-xs text-slate-500">{lookup("course_id", r.course_id) || ""}</p></div> },
  { label: "Kunci", render: (r) => <span className="font-mono font-bold">{"ABCDE"[r.answer_index]}</span> },
];

export const EXAM_FIELDS = [
  { name: "title", label: "Judul ujian", required: true },
  { name: "description", label: "Deskripsi", type: "textarea" },
  { name: "course_id", label: "Kurikulum", type: "select", allowEmpty: true, optionsFrom: COURSE_OPTS },
  { name: "duration_min", label: "Durasi (menit)", type: "number", default: 60 },
  { name: "question_ids", label: "Soal dari bank soal", type: "multiselect", optionsFrom: { url: "/admin/questions", label: (q) => `${q.topic ? `[${q.topic}] ` : ""}${q.text}` } },
  { name: "package_ids", label: "Akses paket", type: "multiselect", optionsFrom: PKG_OPTS, hint: PKG_HINT },
  { name: "show_review", label: "Tampilkan pembahasan setelah submit", type: "checkbox", default: true },
];

export const EXAM_COLUMNS = [
  { label: "Ujian", render: (r) => <p className="font-medium text-slate-900">{r.title}</p> },
  { label: "Soal", render: (r) => r.question_ids?.length || 0 },
  { label: "Durasi", render: (r) => `${r.duration_min} menit` },
  { label: "Akses", render: pkgNames },
];

export const CLASS_FIELDS = [
  { name: "title", label: "Judul kelas", required: true },
  { name: "course_id", label: "Kurikulum", type: "select", allowEmpty: true, optionsFrom: COURSE_OPTS },
  { name: "teacher_id", label: "Pengajar", type: "select", allowEmpty: true, optionsFrom: TEACHER_OPTS },
  { name: "start_at", label: "Waktu mulai", type: "datetime", required: true },
  { name: "duration_min", label: "Durasi (menit)", type: "number", default: 90 },
  { name: "meeting_url", label: "Link Zoom / Google Meet", placeholder: "https://..." },
  { name: "recording_url", label: "Link rekaman (opsional)", placeholder: "https://..." },
  { name: "package_ids", label: "Akses paket", type: "multiselect", optionsFrom: PKG_OPTS, hint: PKG_HINT },
];

export const CLASS_COLUMNS = [
  { label: "Kelas", render: (r, lookup) => <div><p className="font-medium text-slate-900">{r.title}</p><p className="text-xs text-slate-500">{lookup("teacher_id", r.teacher_id) || "-"}</p></div> },
  { label: "Jadwal", render: (r) => <div><p>{fmtDateTime(r.start_at)}</p><p className="text-xs text-slate-500">{r.duration_min} menit</p></div> },
  { label: "Link", render: (r) => <div className="text-xs space-y-1">{r.meeting_url ? <a href={r.meeting_url} target="_blank" rel="noreferrer" className="text-amber-600 underline block">Meeting</a> : "-"}{r.recording_url && <a href={r.recording_url} target="_blank" rel="noreferrer" className="text-amber-600 underline block">Rekaman</a>}</div> },
  { label: "Akses", render: pkgNames },
];

export const ANNOUNCEMENT_FIELDS = [
  { name: "title", label: "Judul", required: true },
  { name: "body", label: "Isi pengumuman", type: "textarea", required: true },
  { name: "pinned", label: "Sematkan", type: "checkbox" },
];

export const ANNOUNCEMENT_COLUMNS = [
  { label: "Pengumuman", render: (r) => <div><p className="font-medium text-slate-900">{r.title}</p><p className="text-xs text-slate-500 line-clamp-2 max-w-lg">{r.body}</p></div> },
  { label: "Dibuat", render: (r) => <div><p>{fmtDate(r.created_at)}</p><p className="text-xs text-slate-500">{r.created_by}</p></div> },
];

export const ASSIGNMENT_FIELDS = [
  { name: "title", label: "Judul tugas", required: true },
  { name: "description", label: "Instruksi", type: "textarea" },
  { name: "course_id", label: "Kurikulum", type: "select", allowEmpty: true, optionsFrom: COURSE_OPTS },
  { name: "due_at", label: "Tenggat", type: "datetime" },
  { name: "max_score", label: "Nilai maksimal", type: "number", default: 100 },
  { name: "package_ids", label: "Akses paket", type: "multiselect", optionsFrom: PKG_OPTS, hint: PKG_HINT },
];

export const ASSIGNMENT_COLUMNS = [
  { label: "Tugas", render: (r) => <p className="font-medium text-slate-900">{r.title}</p> },
  { label: "Tenggat", render: (r) => fmtDateTime(r.due_at) },
  { label: "Akses", render: pkgNames },
];
